/* 新建一个 cos.js，导出 cos 实例 */
import COS from 'cos-js-sdk-v5';
import { getCosSign } from '@/api/api';
import { ResDataInterface } from '@/types/api.interface';
import { AxiosResponse } from 'axios';

type DataCosInterface = {
	credentials: {
		tmpSecretId: string;
		tmpSecretKey: string;
		sessionToken: string;
	};
	startTime: number;
	expiredTime: number;
};

export const cos = new COS({
	// 使用 https 协议
	Protocol: 'https',
	// Domain: 'cos.aidoaido.com',
	// Domain: 'usercos.aidoaido.com',
	// getAuthorization 必选参数
	getAuthorization(options, callback) {
		// 异步获取临时密钥
		getCosSign()
			.then(
				(res: AxiosResponse<ResDataInterface<DataCosInterface>>) => res.data
			)
			.then((res: ResDataInterface<DataCosInterface>) => {
				if (res?.code === 0) {
					const { credentials } = res.data;

					callback({
						TmpSecretId: credentials.tmpSecretId,
						TmpSecretKey: credentials.tmpSecretKey,
						SecurityToken: credentials.sessionToken,
						// 建议返回服务器时间作为签名的开始时间，避免用户浏览器本地时间偏差过大导致签名错误
						StartTime: res.data?.startTime, // 时间戳，单位秒，如：1580000000
						ExpiredTime: res.data?.expiredTime, // 时间戳，单位秒，如：1580000000
					});
				}
			});
	},
});

// 获取cos对象访问 URL
// 腾讯云参考：https://cloud.tencent.com/document/product/436/57420
export const getImageUrl = async (key: string, bucket: string = 'avatars-1305484239', region: string = 'ap-nanjing'): Promise<string> => {
	try {
		const promiseUrl = new Promise<string>((resolve, reject) => {
			cos.getObjectUrl(
				{
					Bucket: bucket,
					Region: region,
					Key: key,
				},
				(err, data) => {
					if (err) {
						reject(err);
					} else {
						resolve(data.Url || '');
					}
				}
			);
		});

		const url = await promiseUrl;
		return url;
	} catch (error) {
		console.error('Error fetching image URL:', error);
		throw error;
	}
};

// 上传文件到 COS
export const uploadFile = async (file: File, key: string) => {
	return new Promise<{ url: string; key: string }>((resolve, reject) => {
		cos.uploadFile(
			{
				Bucket: 'avatars-1305484239',
				Region: 'ap-nanjing',
				Key: key,
				Body: file,
				onProgress: (progressData) => {
					console.log('上传进度:', JSON.stringify(progressData));
				},
			},
			async (err) => {
				if (err) {
					reject(err);
					return;
				}
				try {
					const url = await getImageUrl(key);
					resolve({ url, key });
				} catch (error) {
					reject(error);
				}
			}
		);
	});
};

// 从URL下载图片并上传到COS
export const uploadImageFromUrl = async (imageUrl: string, key: string) => {
	console.log('uploadImageFromUrl', imageUrl, key);
	try {
		const imageBlob = await new Promise<Blob>((resolve, reject) => {
			const img = new Image();
			img.crossOrigin = 'anonymous'; // Attempt to enable CORS
			img.onload = () => {
				const canvas = document.createElement('canvas');
				canvas.width = img.width;
				canvas.height = img.height;
				const ctx = canvas.getContext('2d');
				if (!ctx) {
					return reject(new Error('Failed to get canvas context'));
				}
				ctx.drawImage(img, 0, 0);
				// Try to preserve original type, or default
				let requestedMimeType = 'image/png'; // Default
				const urlPath = imageUrl.split('?')[0];
				const extension = urlPath.split('.').pop()?.toLowerCase();
				if (extension === 'jpg' || extension === 'jpeg') {
					requestedMimeType = 'image/jpeg';
				} else if (extension === 'webp') {
					requestedMimeType = 'image/webp';
				}
				
				canvas.toBlob((blob) => {
					if (blob) {
						resolve(blob);
					} else {
						reject(new Error('Canvas toBlob returned null'));
					}
				}, requestedMimeType);
			};
			img.onerror = (error) => {
				console.error('Image loading error:', error);
				let errorMessage = 'Image loading failed.';
				if (typeof error === 'string') {
					errorMessage += ` Details: ${error}`;
				} else if (error instanceof Event && img.src) {
					// Check if it's a CORS error by trying to fetch again (could be redundant or lead to same error)
					// For simplicity, just provide a generic CORS message.
					if (img.src.startsWith('http') && !img.src.startsWith(window.location.origin)) {
						errorMessage += ` Possible CORS issue with URL: ${img.src}. Ensure the server sets 'Access-Control-Allow-Origin'.`;
					}
				}
				reject(new Error(errorMessage));
			};
			img.src = imageUrl;
		});

		// 从URL或Content-Type推断文件类型 (blob.type should be set by toBlob)
		const mimeType = imageBlob.type;
		let fileName = key.split('/').pop() || 'image'; // Use key for filename base

		// Adjust fileName based on mimeType if necessary (similar to original logic)
		if (mimeType === 'image/jpeg' && !(fileName.endsWith('.jpg') || fileName.endsWith('.jpeg'))) {
			fileName = `${fileName}.jpg`;
		} else if (mimeType === 'image/png' && !fileName.endsWith('.png')) {
			fileName = `${fileName}.png`;
		} else if (mimeType === 'image/gif' && !fileName.endsWith('.gif')) { // Note: canvas.toBlob might not support GIF well, usually converts to PNG/JPEG
			fileName = `${fileName}.gif`;
		} else if (mimeType === 'image/webp' && !fileName.endsWith('.webp')) {
			fileName = `${fileName}.webp`;
		} else if (!fileName.includes('.')) { // Add extension if missing
			const extFromMime = mimeType.split('/')[1];
			if (extFromMime) fileName = `${fileName}.${extFromMime}`;
		}

		// Creating File object
		const file = new File([imageBlob], fileName, {
			type: mimeType
		});
		
		console.log(`Successfully processed image via canvas: ${fileName}, 大小: ${(file.size / 1024).toFixed(2)}KB, 类型: ${mimeType}`);
		
		// 使用现有的uploadFile函数上传到COS
		const result = await uploadFile(file, key);
		return result;
		
	} catch (error) {
		console.error('图片处理或上传失败:', error);
		throw new Error(`图片处理失败: ${error instanceof Error ? error.message : '未知错误'}`);
	}
};


